import { Button } from "primereact/button";
import styled from "styled-components";
import {
  IBenefitLineSelection,
  IVocabularyRow,
} from "types/BenefitSelection/BenefitSelection";

import { BENEFIT_LABELS, CONFIGURABLE_BENEFITS } from "./constants";
import {
  definitionsOfLineType,
  describeFallback,
  describeLineTypeDefinition,
  describeSelector,
  describeTieBreak,
} from "./describeSelector";
import { selectorsWithoutFallback } from "./mapper";

const BenefitRow = styled.div`
  display: grid;
  grid-template-columns: minmax(9rem, 1fr) 4fr minmax(9rem, 1fr) auto;
  gap: 1rem;
  padding: 1.25rem 0;
  border-top: 1px solid var(--surface-200);
  align-items: start;
`;

const SelectorLine = styled.div`
  display: grid;
  grid-template-columns: 1.5rem 1fr;
  gap: 0.5rem;
  padding: 0.2rem 0;
`;

interface ILineSelectionTableProps {
  /** The saved list per benefit. `null` means the built-in default is in force. */
  lineSelection: Record<string, IBenefitLineSelection | null>;
  lineSelectionDefaults: Record<string, IBenefitLineSelection>;
  physicianCopayBilled: boolean;
  /** EVERY line type definition, global and payer-scoped — `GET /benefitLineVocabulary`. */
  vocabulary: IVocabularyRow[];
  onEdit: (benefit: string) => void;
}

/**
 * CLE-2792 — step 3, read-only: which line inside the service line answers each benefit.
 *
 * 🚨 EVERY SELECTOR IS LISTED AND EVERY LINE TYPE IS EXPANDED INLINE. Not a summary, not the first
 * one, not a count with a link — the whole list, as sentences, with the words each type resolves to
 * printed beside it, for every payer that defines it. The point of moving these out of code was that
 * nobody could see them; a row reading "3 selectors · edit" would put them back out of sight.
 *
 * 🚨 A BENEFIT WITH NOTHING SAVED IS PRINTED IN FULL AND UNLABELLED, exactly like a saved one. A
 * pill saying "Default" was the loudest thing in the row and said the least: what a reader needs is
 * what the account reads, and the default is a real, effective policy either way.
 */
export function LineSelectionTable({
  lineSelection,
  lineSelectionDefaults,
  physicianCopayBilled,
  vocabulary,
  onEdit,
}: ILineSelectionTableProps) {
  return (
    <div
      className="flex flex-column"
      data-testid="benefitSelection.lineSelectionTable"
    >
      {CONFIGURABLE_BENEFITS.map((benefit) => {
        const effective =
          lineSelection[benefit] ?? lineSelectionDefaults[benefit];
        const notBilled =
          benefit === "PROFESSIONAL_COPAY" && !physicianCopayBilled;
        const selectors = effective ? selectorsWithoutFallback(effective) : [];

        return (
          <BenefitRow
            key={benefit}
            data-testid={`benefitSelection.line.${benefit}`}
          >
            <div className="flex flex-column gap-2">
              <span className="font-medium text-sm">
                {BENEFIT_LABELS[benefit]}
              </span>
            </div>

            <div className="flex flex-column gap-1">
              {notBilled ? (
                // In the selectors' own column, so the three benefit rows line up.
                <SelectorLine>
                  <span />
                  <span className="text-sm text-color-secondary">
                    Not billed at this account.
                  </span>
                </SelectorLine>
              ) : (
                <>
                  {selectors.map((selector, index) => {
                    const definitions = selector.role
                      ? definitionsOfLineType(selector.role, vocabulary)
                      : [];

                    return (
                      <SelectorLine
                        // Ordered list, no identity of its own — the index is the key.
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${benefit}-selector-${index}`}
                        data-testid={`benefitSelection.line.${benefit}.selector.${index}`}
                      >
                        <span className="text-sm text-color-secondary">
                          {index + 1}.
                        </span>
                        <span className="flex flex-column gap-1">
                          <span className="text-sm">
                            {describeSelector(selector)}
                          </span>
                          {definitions.map((row) => (
                            <span
                              key={row.id}
                              className="text-xs text-color-secondary"
                              data-testid={`benefitSelection.line.${benefit}.selector.${index}.definition.${row.id}`}
                            >
                              {describeLineTypeDefinition(row)}
                            </span>
                          ))}
                          {selector.role && definitions.length === 0 && (
                            <span
                              className="text-xs text-orange-700"
                              data-testid={`benefitSelection.line.${benefit}.selector.${index}.noDefinition`}
                            >
                              No payer defines this line type. Every check
                              declines this benefit and falls back until a
                              definition exists.
                            </span>
                          )}
                        </span>
                      </SelectorLine>
                    );
                  })}
                  {effective && (
                    <SelectorLine>
                      <span />
                      <span
                        className="text-sm text-color-secondary"
                        data-testid={`benefitSelection.line.${benefit}.fallback`}
                      >
                        {describeFallback(effective)}
                      </span>
                    </SelectorLine>
                  )}
                </>
              )}
            </div>

            {notBilled || !effective ? (
              <span className="text-sm">—</span>
            ) : (
              <span className="flex flex-column gap-1">
                <span className="text-xs text-color-secondary">
                  If several match
                </span>
                <span
                  className="text-sm"
                  data-testid={`benefitSelection.line.${benefit}.tieBreak`}
                >
                  {describeTieBreak(effective, benefit)}
                </span>
              </span>
            )}

            <Button
              type="button"
              label="Edit"
              className="p-button-outlined p-button-sm"
              disabled={notBilled}
              data-testid={`benefitSelection.line.${benefit}.edit`}
              onClick={() => onEdit(benefit)}
            />
          </BenefitRow>
        );
      })}
    </div>
  );
}
